
import { useStore } from "vuex";
import type { SizeType } from "ant-design-vue/es/config-provider";
import {
  defineComponent,
  ref,
  onMounted,
  computed,
  watch,
} from "@vue/runtime-core";
import {
  EditOutlined,
  DeleteOutlined,
  RiseOutlined,
  FallOutlined,
} from "@ant-design/icons-vue";

import router from "@/router";
import Header from "@/components/Header.vue";
import { debounce } from "../utils/debounce";
import Loading from "../components/Loading.vue";
import { ToastService } from "@/services/toast";
import { toastError } from "../utils/toastError";
import { IAllergyResponse } from "@/types/allergies";
import { isUserTheAdmin } from "@/composables/isAdmin";
import { allergiesService } from "@/services/allergies";

export default defineComponent({
  name: "Dashboard",
  components: {
    Header,
    Loading,
    EditOutlined,
    DeleteOutlined,
    RiseOutlined,
    FallOutlined,
  },
  setup() {
    const store = useStore();
    const toast = new ToastService();
    const isAdmin = isUserTheAdmin();

    const page = ref(1);
    const total = ref(0);
    const pageSize = ref(3);
    const searchTerm = ref("");
    const size = ref<SizeType>("large");

    const currentUser = computed(() => store.state?.auth?.user?.name);
    const isLoading = computed(() => store.state.allergies.isLoading);
    const allergies = computed(() => store.state.allergies.allAllergies);

    const fetchAllAllergies = () =>
      store.dispatch("allergies/fetchAllergies", {
        pageSize: pageSize.value,
        page: page.value,
      });

    function deleteAllergy(id: number) {
      allergiesService
        .deleteAllergy(id)
        .then(() => {
          toast.warning("Allergy deleted.");
          fetchAllAllergies();
        })
        .catch((err) => toastError(err));
    }

    function markAsHighRisk(allergy: IAllergyResponse, isHighRisk: boolean) {
      allergiesService
        .markAsHighRisk(isHighRisk, allergy.id)
        .then(() => {
          isHighRisk
            ? toast.success(`${allergy.name} is now marked as high risk.`)
            : toast.warning(
                `${allergy.name} is no longer marked as high risk.`
              );
          fetchAllAllergies();
        })
        .catch((err) => toastError(err));
    }

    onMounted(() => {
      fetchAllAllergies();
      findTotal();
    });

    function findTotal() {
      allergiesService
        .getAllergies(1, 100)
        .then((data) => (total.value = data.length))
        .catch((err) => toastError(err));
    }

    watch([page, pageSize], () => {
      store.dispatch("allergies/fetchAllergies", {
        pageSize: pageSize.value,
        page: page.value,
      });
    });

    watch(
      searchTerm,
      debounce(() =>
        store.dispatch("allergies/searchAllergies", searchTerm.value)
      )
    );

    return {
      toast,
      isLoading,
      allergies,
      size,
      searchTerm,
      page,
      pageSize,
      total,
      currentUser,
      isAdmin,
      router,
      deleteAllergy,
      markAsHighRisk,
    };
  },
  inheritAttrs: false,
});
